import { useEffect, useState } from 'react';
import type { TenantProgramContext } from '@starter/shared';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

export function ProgramContextSelect() {
  const { user, refreshUser } = useAuth();
  const [contexts, setContexts] = useState<TenantProgramContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (user?.authKind !== 'tenant') {
      setContexts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .listTenantContexts()
      .then((res) => setContexts(res.data))
      .catch(() => setContexts([]))
      .finally(() => setLoading(false));
  }, [user?.authKind, user && user.authKind === 'tenant' ? user.programId : null]);

  if (user?.authKind !== 'tenant' || loading || contexts.length <= 1) {
    return null;
  }

  const activeProgramId = user.programId;

  async function handleChange(programId: string) {
    if (programId === activeProgramId || switching) return;
    setSwitching(true);
    try {
      await api.setTenantContext(programId);
      await refreshUser();
      window.location.assign(
        contexts.find((c) => c.programId === programId)?.role === 'STUDENT'
          ? '/student'
          : contexts.find((c) => c.programId === programId)?.role === 'INSTRUCTOR'
            ? '/instructor'
            : '/pd',
      );
    } catch (err) {
      console.error(err instanceof ApiError ? err.message : err);
    } finally {
      setSwitching(false);
    }
  }

  return (
    <label className="program-context-select">
      <span className="program-context-select__label">Program</span>
      <select
        value={activeProgramId}
        disabled={switching}
        onChange={(e) => void handleChange(e.target.value)}
        aria-label="Select program context"
      >
        {contexts.map((ctx) => (
          <option key={ctx.programId} value={ctx.programId}>
            {ctx.programName ?? 'Program'} · {ctx.role.replace('_', ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}
